import { AdminScope } from '@pubstack/common/src/scope'
import { FunctionComponent, useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAdminScopes } from '~/api/admin/admin-scope.api'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { TabProp } from '~/components/Tabs'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureAdminScopeDetail } from './PureAdminScopeDetail'

const ADMIN_TABS: Pick<TabProp, 'label' | 'iconName' | 'trackingLabel'>[] = [
  {
    label: 'Sites',
    iconName: 'site',
    trackingLabel: 'common-admin-scopes-detail-sites-tab-apply',
  },
  {
    label: 'Features',
    iconName: 'inputs',
    trackingLabel: 'common-admin-scopes-detail-features-tab-apply',
  },
  {
    label: 'Ad Management settings',
    iconName: 'rocket',
    trackingLabel: 'adm-admin-scopes-detail-settings-tab-apply',
  },
  {
    label: 'Prebid server',
    iconName: 'server',
    trackingLabel: 'adm-admin-scopes-detail-prebidserver-tab-apply',
  },
  {
    label: 'POC-overrides',
    iconName: 'fire',
    trackingLabel: 'adm-admin-scopes-detail-pocoverrides-tab-apply',
  },
  {
    label: 'Site config',
    iconName: 'fire',
    trackingLabel: 'adm-admin-scopes-detail-siteconfig-tab-apply',
  },
  {
    label: 'GAM manager',
    iconName: 'shipping',
    trackingLabel: 'adm-admin-scopes-detail-gam-account-tab-apply',
  },
] as const

type TabLabel = (typeof ADMIN_TABS)[number]['label']

const tabLabelToPath: { [key in TabLabel]: string } = {
  Sites: 'sites',
  Features: 'features',
  'API keys': 'api',
  'Ad Management settings': 'adm-settings',
  'Prebid server': 'adm-prebid-server',
  'POC-overrides': 'poc-overrides',
  'Site config': 'site-config',
  'GAM manager': 'gam-manager',
}

export const AdminScopeDetail: FunctionComponent = () => {
  // Context
  const { scopeId } = useParams()

  // API hooks
  const { data: scopes, isLoading: isLoadingScopes } = useAdminScopes()

  // Memoized values
  const currentAdminScope: AdminScope | undefined = useMemo(() => scopes.find((s) => s.id === scopeId) ?? scopes[0], [scopes, scopeId])
  const enabledScopes: AdminScope[] = useMemo(() => scopes.filter((scope) => scope.enabled) || [], [scopes])

  // Hooks
  const navigate = useNavigate()
  const location = useLocation()
  const breadcrumbs = useBreadcrumbs(currentAdminScope)

  const tabLabelFromLocation = (Object.keys(tabLabelToPath) as Array<keyof typeof tabLabelToPath>).find(
    (key) => tabLabelToPath[key] === (location.pathname.split('/').find((dir) => Object.values(tabLabelToPath).includes(dir)) ?? 'sites')
  )

  const onScopeChange = (option: SelectOptionProp) => {
    if (!enabledScopes.some((s) => s.id === option.value)) {
      return
    }
    if (option.value !== scopeId && scopeId) {
      const to = location.pathname.replace(scopeId, option.value as string)
      navigate(to)
    }
  }

  useEffect(() => {
    if (isLoadingScopes) return

    if (!scopes.find((s) => s.id === scopeId)) {
      navigate('/analytics/overview')
    }
  }, [isLoadingScopes, scopes, scopeId])

  return (
    <PureAdminScopeDetail
      breadcrumbs={breadcrumbs}
      scopes={enabledScopes}
      currentScope={currentAdminScope}
      onScopeChange={onScopeChange}
      onTabClick={(tab) => {
        navigate(tabLabelToPath[tab.label as TabLabel])
      }}
      tabs={ADMIN_TABS.map((t) => ({ ...t, active: t.label === tabLabelFromLocation }))}
    >
      <Outlet />
    </PureAdminScopeDetail>
  )
}
